import { NavLink } from "react-router-dom";

import { Time } from "../../../../shared/types/time";
import { useTimes } from "../../queries/time";
import { useLocale } from "../../stores/locale";
import { dateFormat } from "../../utils/date-format";
import { getWeek } from "../../utils/week";
import { TotalTimer } from "./total-timer";

export function TimeWeek({ date }: { date: Date }) {
  const { locale: t, currentLocaleName: localeName } = useLocale(
    (state) => state,
  );

  const weekDays = getWeek(date);

  if (!weekDays[0] || !weekDays[6]) {
    throw new Error("Invalid week days");
  }

  const { data: times } = useTimes({
    from: firstSecondOfDay(weekDays[0]),
    to: lastSecondOfDay(weekDays[6]),
  });

  if (!times) {
    return;
  }

  return (
    <div className="grid grid-cols-8 w-full border-b-2 border-gray-200">
      {weekDays.map((day) => (
        <NavLink
          key={day.toISOString()}
          className={`flex flex-col ${isCurrentDate(day, date) ? "font-bold" : ""}`}
          to={`/time?date=${dateFormat(day)}`}
        >
          <span>
            {getWeekDayShort(day, localeName)} (
            {getShortDateFormat(day, localeName)})
          </span>
          <TotalTimer
            flashColonIfActive={false}
            times={timesForDate(day, times)}
          />
        </NavLink>
      ))}
      <div className="flex items-center justify-center">
        {t.time.total}: <TotalTimer flashColonIfActive={false} times={times} />
      </div>
    </div>
  );

  function timesForDate(date: Date, times: Time[]): Time[] {
    return times.filter(
      (time) => time.from >= startOfDay(date) && time.from <= endOfDay(date),
    );
  }
}

function startOfDay(date: Date): Date {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay;
}

function endOfDay(date: Date): Date {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
}

function isCurrentDate(date1: Date, date2: Date): boolean {
  return date1.toISOString() === date2.toISOString();
}

function getWeekDayShort(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { weekday: "short" });
}

function getShortDateFormat(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
  });
}

function firstSecondOfDay(date: Date) {
  // Get date at 00:00:00
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function lastSecondOfDay(date: Date) {
  // Get date at 23:59:59
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
  );
}
